import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CreatorSliderContentfulModel } from 'src/api/models/CreatorsSlider';
import Paragraph from 'src/elements/typography/paragraph';
import {
  CreatorImagesContainer,
  ImageCaption,
  CreatorItem, CreatorImage, DotsContainer, DotItem, CreatorImageOverlay, CreatorImageContainer
} from './creators.images.styles';
import Slider from 'react-slick'
import { LeftArrow, RightArrow } from 'src/elements/slider/slider.arrows.styles'

export interface CreatorImagesProps {
  creators: CreatorSliderContentfulModel['contentfulchildren']
}

const CreatorImages = ({ creators }: CreatorImagesProps): JSX.Element => {
  const creatorsSliderContainerRef = useRef<HTMLDivElement>(null);
  const [ activeSlider, setActiveSlider ] = useState(0);
  const [ isDragging, setIsDragging ] = useState(false);
  const [ isSwiping, setIsSwiping ] = useState(false);
  const [ imagesDisplayed, setImagesDisplayed ] = useState(3);

  const updateImagesDisplayed = () => {
    if (creatorsSliderContainerRef) {
      const screenWidth = (creatorsSliderContainerRef.current as HTMLDivElement).getBoundingClientRect().width;

      if (screenWidth <= 700) {
        setImagesDisplayed(1)
      } else if (screenWidth <= 950) {
        setImagesDisplayed(2)
      } else {
        setImagesDisplayed(3)
      }
    }
  }

  useEffect(() => {
    updateImagesDisplayed()
    window.addEventListener('resize', updateImagesDisplayed);
    return () => {
      window.removeEventListener('resize', updateImagesDisplayed);
    }
  }, [ creatorsSliderContainerRef, updateImagesDisplayed ]);

  const sliderSettings = {
    infinite: false,
    swipe: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <RightArrow isHidden={activeSlider === creators.length - 3}/>,
    prevArrow: <LeftArrow isHidden={activeSlider === 0}/>,
    // eslint-disable-next-line react/display-name
    appendDots: (dots: any) => (
      <DotsContainer>
        {dots}
      </DotsContainer>
    ),
    beforeChange: (_oldIndex: number, newIndex: number) => {
      setActiveSlider(newIndex);
      setIsDragging(true);
    },
    afterChange: () => {
      setIsDragging(false);
    },
    // eslint-disable-next-line react/display-name
    customPaging: (index: number) => (
      <DotItem active={activeSlider === index}/>
    ),
    swipeEvent: () => {
      setIsSwiping(true)

      setTimeout(() => setIsSwiping(false), 1000) // If the user starts to drag but stays in the same slide
    },
    onSwipe: () => setIsSwiping(false),
    responsive: [
      {
        breakpoint: 950, // layout.subLg
        settings: {
          slidesToShow: 2,
          nextArrow: <RightArrow isHidden={activeSlider === creators.length - 2}/>
        }
      },
      {
        breakpoint: 800, // layout.subMd
        settings: {
          dots: true,
          arrows: false,
          slidesToShow: 1
        }
      }
    ],
  }

  const isCreatorSlideAnimated = useCallback((index: number) => {
    return index === activeSlider - 1 || index === activeSlider + imagesDisplayed;
  }, [ activeSlider ]);

  return (
    <CreatorImagesContainer ref={creatorsSliderContainerRef}>
      <Slider {...sliderSettings} >
        {
          creators.map((creator, index) => (
            <CreatorItem
              key={index}
              to={`/case-studies/${creator.slug}`}
              onClick={(event) => (isDragging || isSwiping) && event.preventDefault()}
              onDragStart={(event) => {
                event.stopPropagation()
                event.preventDefault()
              }}
              onDrag={(event) => {
                event.stopPropagation()
                event.preventDefault()
              }}>
              <CreatorImageContainer animated={isCreatorSlideAnimated(index) && !isSwiping}>
                <CreatorImageOverlay />
                <CreatorImage image={creator.image.gatsbyImageData} alt={creator.childContentfulSliderItemDescriptionTextNode.description} />
              </CreatorImageContainer>
              <ImageCaption>
                <Paragraph
                  text={creator.childContentfulSliderItemDescriptionTextNode.description}
                  size="1.5em"
                  sizeXs="1em"
                  sizeSm="1em"
                  weight="normal"
                  align="center"
                />
              </ImageCaption>
            </CreatorItem>
          ))
        }
      </Slider>
    </CreatorImagesContainer>
  )
}

export default CreatorImages;
